import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type FontSize = 'small' | 'medium' | 'large';

interface FontSizeContextType {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  getFontSizeClass: () => string;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};

interface FontSizeProviderProps {
  children: ReactNode;
}

export const FontSizeProvider: React.FC<FontSizeProviderProps> = ({ children }) => {
  const [fontSize, setFontSizeState] = useState<FontSize>('medium');

  useEffect(() => {
    // Загружаем настройки размера шрифта из localStorage
    const savedFontSize = localStorage.getItem('fontSize') as FontSize;
    if (savedFontSize && ['small', 'medium', 'large'].includes(savedFontSize)) {
      setFontSizeState(savedFontSize);
    }
  }, []);

  const setFontSize = (size: FontSize) => {
    setFontSizeState(size);
    localStorage.setItem('fontSize', size);
    
    // Применяем CSS переменные для размера шрифта
    const root = document.documentElement;
    switch (size) {
      case 'small':
        root.style.setProperty('--font-size-base', '14px');
        root.style.setProperty('--font-size-sm', '12px');
        root.style.setProperty('--font-size-lg', '16px');
        root.style.setProperty('--font-size-xl', '18px');
        root.style.setProperty('--font-size-2xl', '20px');
        root.style.setProperty('--font-size-3xl', '24px');
        break;
      case 'medium':
        root.style.setProperty('--font-size-base', '16px');
        root.style.setProperty('--font-size-sm', '14px');
        root.style.setProperty('--font-size-lg', '18px');
        root.style.setProperty('--font-size-xl', '20px');
        root.style.setProperty('--font-size-2xl', '24px');
        root.style.setProperty('--font-size-3xl', '30px');
        break;
      case 'large':
        root.style.setProperty('--font-size-base', '18px');
        root.style.setProperty('--font-size-sm', '16px');
        root.style.setProperty('--font-size-lg', '20px');
        root.style.setProperty('--font-size-xl', '22px');
        root.style.setProperty('--font-size-2xl', '28px');
        root.style.setProperty('--font-size-3xl', '36px');
        break;
    }
  };

  const getFontSizeClass = () => {
    switch (fontSize) {
      case 'small':
        return 'text-sm';
      case 'medium':
        return 'text-base';
      case 'large':
        return 'text-lg';
      default:
        return 'text-base';
    }
  };

  // Применяем размер шрифта при инициализации
  useEffect(() => {
    // Применяем размер шрифта только при первом рендере
    const root = document.documentElement;
    switch (fontSize) {
      case 'small':
        root.style.setProperty('--font-size-base', '14px');
        root.style.setProperty('--font-size-sm', '12px');
        root.style.setProperty('--font-size-lg', '16px');
        root.style.setProperty('--font-size-xl', '18px');
        root.style.setProperty('--font-size-2xl', '20px');
        root.style.setProperty('--font-size-3xl', '24px');
        break;
      case 'medium':
        root.style.setProperty('--font-size-base', '16px');
        root.style.setProperty('--font-size-sm', '14px');
        root.style.setProperty('--font-size-lg', '18px');
        root.style.setProperty('--font-size-xl', '20px');
        root.style.setProperty('--font-size-2xl', '24px');
        root.style.setProperty('--font-size-3xl', '30px');
        break;
      case 'large':
        root.style.setProperty('--font-size-base', '18px');
        root.style.setProperty('--font-size-sm', '16px');
        root.style.setProperty('--font-size-lg', '20px');
        root.style.setProperty('--font-size-xl', '22px');
        root.style.setProperty('--font-size-2xl', '28px');
        root.style.setProperty('--font-size-3xl', '36px');
        break;
    }
  }, []); // Убираем fontSize из зависимостей

  const value: FontSizeContextType = {
    fontSize,
    setFontSize,
    getFontSizeClass,
  };

  return <FontSizeContext.Provider value={value}>{children}</FontSizeContext.Provider>;
};

